import {
  type UseQueryOptions,
  type UseQueryResult,
  useQuery,
} from '@tanstack/react-query';
import { UserSessionsApi } from '../api/user-sessions';
import { LoginStatus, type User } from '@/entities/user/user/@x/session';
import { AUTH_SESSION_KEY, initialSessionState } from './auth-session-state';

export const loginBySessionConfig: UseQueryOptions<User> = {
  queryKey: ['profile'],
  queryFn: ({ signal }) =>
    UserSessionsApi.getProfile({ signal, timeout: 15000 }).then(
      (response) => response.data,
    ),
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  refetchOnMount: false,
  retry: false,
  useErrorBoundary: false,
  cacheTime: Infinity,
  staleTime: Infinity,
};

export const useAuthSession = () =>
  useQuery({
    queryKey: AUTH_SESSION_KEY,
    queryFn: () => initialSessionState,
    initialData: initialSessionState,
    enabled: false,
    cacheTime: Infinity,
    staleTime: Infinity,
    useErrorBoundary: false,
  }).data;

export const useLoginStatus = () => useAuthSession().loginStatus;
export const useIsAuth = () => useLoginStatus() === LoginStatus.login;
export const useIsExpired = () => {
  const state = useAuthSession();
  return state.loginStatus === LoginStatus.expired || state.reauthenticating;
};

export function useUser<T extends boolean = false>(
  returnQuery?: T,
): T extends false | undefined ? User : UseQueryResult<User, unknown> {
  const isAuth = useIsAuth();
  const query = useQuery({ ...loginBySessionConfig, enabled: isAuth });

  return (returnQuery ? query : query.data) as T extends false | undefined
    ? User
    : UseQueryResult<User, unknown>;
}
